import React, { useState } from "react";
import FeatherIcon from "feather-icons-react";
import {handleItemClick, buildProfileAction, getTeamName, formatLastName, getOrdinalSuffix} from "../../../constants";
import SelectInput from "../../../components/SelectInput";
import BallIcon from "../../../components/BallIcon";
import ParticipantActionsModal from "../../../components/ParticipantActionsModal";
import { toast } from "react-toastify";
import { useTheme } from "../../../contexts/ThemeContext";
import { chipsMatchEntity, searchStateClass } from "../../../utils/searchMatch";
import { normalizeTeamPlayers } from "../../../utils/normalizeTeamPlayers";
import useAxios from "../../../utils/useAxios";

const TournamentGroupTable = ({
                                  group,
                                  tournament,
                                  tournamentId,
                                  is_authorized,
                                  activeStage,
                                  stages,
                                  startPlace,
                                  participants,
                                  setGroups,
                                  fetchParticipants,
                                  fetchGroups,
                                  setModalData,
                                  setModalOpen,
                                  expandMatch,
                                  matchOffset,
                                  t,
                                  history,
                                  location,
                                  pickedPlayer,
                                  pickerTarget,
                                  pickedCandidate,
                                  onPlaceCandidate,
                                  onCancelCandidate,
                                  isTouchDevice,
                                  onPlayerTouchStart,
                                  onStartMove,
                                  onCellTap,
                                  onDragStartPlayer,
                                  onDropOnCell,
                                  loadingCells = {},
                                  searchChips = [],
                                  teamRoster,
                                  playerNames,
                                  viewMode = "matrix"
                              }) => {
    // Self-managed (not received as a prop) - useAxios() returns a brand
    // new axios instance (with fresh interceptors) on every call, so
    // receiving it as a prop from TournamentGroups.js (itself re-created on
    // every one of ITS renders) meant this component's own React.memo below
    // always saw a "changed" prop and re-rendered regardless of anything
    // else being stable - same pattern Bracket.js/BracketClassic.js already
    // use for the identical reason.
    const api = useAxios();
    // Highlight+dim, not hide - a filtered-out row here would still throw
    // off getMatch's row/col math against every other row (it walks a
    // fixed grid derived from group.number_players), same reasoning as
    // BracketClassic/Bracket's slots not being hidden either. A 'group'
    // chip targeting THIS table's own group highlights every row
    // (including empty slots) regardless of participant, same as
    // Bracket/BracketClassic's 'draw' chips do for a whole bracket. Grade 3
    // also folds in the team's own roster (see fetchTeamRosterMap.js) - this
    // row's own participant object only carries the team's name, not who's
    // actually on it. Grade 1/2 resolve each player's real first name via
    // playerNames (see fetchPlayerNameMap.js) - this row's own participant
    // only carries it truncated to an initial, which a full-name search chip
    // would never match otherwise.
    const resolvePlayer = (p) => (p ? (playerNames?.get(p.id) || p) : p);
    const participantMatchesSearch = (participant) => {
        const names = !participant ? [] : tournament?.grade === 1
            ? [resolvePlayer(participant)]
            : tournament?.grade === 2
                ? (participant.players || []).map(resolvePlayer)
                : [participant.name, ...(teamRoster?.get(participant.name) || [])];
        return chipsMatchEntity(searchChips, { names, groupValue: group.number });
    };
    // Compact Table is a user-facing-only density preference - an admin
    // editing this tournament still gets the regular view regardless of the
    // stored setting, only seeing it once they switch to Public View for
    // this tournament. See Header.js/BottomNav.js for the matching
    // toggle-visibility gate.
    const { compactTable: rawCompactTable } = useTheme();
    const compactTable = rawCompactTable && !is_authorized;
    const playersCount = group.number_players || 4;
    const playerNumbers = Array.from({ length: playersCount }, (_, i) => i + 1);
    // Whole-group dimming: this group's own participants tested exactly like
    // each row's own participantMatchesSearch below - if none of them (nor a
    // 'group' chip targeting this group) match, the whole group fades as one
    // block/card (header, every row, every column) instead of leaving some
    // of its columns (row number, games/points/place) undimmed while only
    // the name/matrix cells did - that partial per-cell dimming read as
    // broken/selective rather than the group actually fading. A group that
    // does contain a match keeps its own per-row highlighting below instead
    // (the matching row stands out, the rest of that same group's rows still
    // dim individually).
    const groupHasMatch = searchChips.length > 0 && Array.from({ length: playersCount }, (_, i) => group.players?.[i]).some(p => participantMatchesSearch(p) === true);
    const groupDimmed = searchChips.length > 0 && !groupHasMatch;
    const [actionsModal, setActionsModal] = useState(null);
    // Free-typed place input (non place-distribution groups only) - tracks
    // what's being typed locally and saves on blur, rather than PATCHing on
    // every keystroke. Saving per-keystroke made Number("") (the value
    // while the field is momentarily empty, e.g. clearing it to type a new
    // digit) round-trip as 0 and flash on screen before the next digit
    // overwrote it.
    const [placeDrafts, setPlaceDrafts] = useState({});

    const getParticipantDeleteId = (participant) =>
        participant?.id ?? (tournament?.grade === 1 ? participant?.player_id : participant?.team_id);

    const getParticipantProfiles = (participant) => {
        if (!participant) return [];
        if (tournament?.grade === 1) {
            const name = `${participant?.first_name ? participant.first_name[0] + '. ' : ''}${formatLastName(participant.last_name)}`;
            return [{ label: name, onClick: () => handleItemClick(participant, history, location) }];
        }
        if (tournament?.grade === 2) {
            // Two profile entries, one per slot - the row itself only ever
            // shows players[0]/players[1], so a pair carrying a third member
            // (duplicate `sorting`, see src/utils/normalizeTeamPlayers.js)
            // offered a third "Профіль" action for someone not even named in
            // the table.
            return normalizeTeamPlayers(participant.players, 2).map((p) => ({
                label: `${p?.first_name ? p.first_name[0] + '. ' : ''}${formatLastName(p?.last_name)}`,
                onClick: () => handleItemClick(p, history, location)
            }));
        }
        return (teamRoster?.get(participant.name) || []).map((p) => buildProfileAction(p, history, location));
    };

    const handlePlayersChange = async (groupId, value) => {
        // Positions beyond the new count get dropped on the backend along
        // with their assigned participant (positions within the new count
        // are preserved) - warn before sending a reduction that would
        // actually lose someone, rather than after the fact.
        const willLoseParticipants = (group.players || []).slice(value).some(p => p);
        if (willLoseParticipants) {
            const confirmed = window.confirm(
                t("Reducing the number of participants will remove players already placed beyond the new count. Continue?")
            );
            if (!confirmed) {
                // Forces a re-render so SelectInput's default_value effect
                // re-fires and resets the dropdown back to the unchanged
                // group.number_players - it already flipped to the picked
                // value on click, before this confirm ran.
                setGroups(prev => [...prev]);
                return;
            }
        }

        try {
            await api.patch(`/api/v1.0/group-stage/${activeStage}/groups/${groupId}/`, { number_players: value });
            await fetchParticipants(activeStage);
            await fetchGroups(activeStage);
        } catch (e) {
            console.error("Failed to update group:", e);
            // The select re-syncs from group.number_players (via fetchGroups
            // above/SelectInput's own default_value effect) - when the PATCH
            // is rejected, fetchGroups never even runs, so the dropdown just
            // silently snaps back to the old count with zero indication why.
            // Surfacing the backend's own message (when it has one) instead
            // of only a generic fallback, since a rejection here is usually
            // a real, specific reason (e.g. a filled slot still on record
            // past the new count) worth showing, not just "didn't save".
            const detail = e.response?.data?.detail || e.response?.data?.number_players?.[0];
            toast.error(detail || t("Failed to update the group's number of participants"));
        }
    };

    // Deletes this whole group directly, mirroring TournamentDrawStagesSettings.js's
    // own per-stage trash icon - without it, removing a single group meant going
    // into Settings and shrinking "Number of groups", which always drops the LAST
    // group (not necessarily the one the admin actually wants gone) and confused
    // admins who didn't realize that field did double duty as a delete.
    const handleDeleteGroup = async () => {
        if (!window.confirm(t("Delete this group? All its matches and results will be lost."))) return;
        try {
            await api.delete(`/api/v1.0/group-stage/${activeStage}/groups/${group.id}/`);
            await fetchParticipants(activeStage);
            await fetchGroups(activeStage);
        } catch (e) {
            console.error("Failed to delete group:", e);
            toast.error(e.response?.data?.detail || t("Failed to delete the group"));
        }
    };

    const openSelectModal = (groupId, position) => {
        setModalData({ tournamentId, stageId: activeStage, groupId, position });
        setModalOpen(true);
    };

    const getMatch = (group, row, col) => {
        if (row === col) return { match: null, index: null };
        const n = group.number_players;
        let index = 0;
        for (let r = 0; r < n; r++) {
            for (let c = r + 1; c < n; c++) {
                if ((r === row && c === col) || (r === col && c === row)) return { match: group.matches[index], index };
                index++;
            }
        }
        return { match: null, index: null };
    };

    // Padel-Americano/Mexicano-style classification row for the standings
    // view - M/W/L aren't stored on the participant (only games/points are,
    // see fetchGroups/updateMatches in TournamentGroups.js), so tally them
    // here off the same group.matches the matrix view already reads per
    // cell via getMatch, rather than adding a parallel backend field.
    // "played" keys off winner_side, not match.status - this endpoint's
    // match objects don't carry a status field (that's a schedule-endpoint
    // thing), and the matrix view itself never checks it either: it colors
    // score-win/score-lose and shows a decided match purely off winner_side
    // being set (see the cell renderer below), including walkover/retired
    // matches that have a winner but no set scores (hasScores false).
    const getRowStats = (rowIndex) => {
        let played = 0, wins = 0, losses = 0;
        for (let col = 0; col < playersCount; col++) {
            if (col === rowIndex) continue;
            const { match } = getMatch(group, rowIndex, col);
            if (!match?.winner_side) continue;
            played++;
            const side = rowIndex < col ? 1 : 2;
            if (match.winner_side === side) wins++; else losses++;
        }
        return { played, wins, losses };
    };

    const playersOptions = Array.from({ length: 10 }, (_, i) => ({
        id: i + 2,
        name: `${i + 2}`
    }));

    // Manual place picker only makes sense once the stage says which places
    // this group is actually playing for - the range is derived from that
    // starting place plus how many participants are in THIS group, not
    // computed from points/games, since scoring/tie-break rules differ per
    // tournament and can't be trusted to rank places automatically.
    const placeOptions = startPlace
        ? Array.from({ length: playersCount }, (_, i) => ({
            id: startPlace + i,
            name: `${startPlace + i}`
        }))
        : null;

    // "Place distribution" groups (startPlace set) are labeled by the place
    // range they're playing for - same wording as a placement bracket's
    // "5th Place" label in the schedule - instead of a letter, since a
    // lettered "Group A" reads as an arbitrary qualifying group here rather
    // than a group that decides specific final places.
    const groupLabel = startPlace
        ? (playersCount > 1
            ? `${startPlace}-${startPlace + playersCount - 1} ${t("Place")}`
            : `${startPlace}${getOrdinalSuffix(startPlace)} ${t("Place")}`)
        : `${t("Group")} ${String.fromCharCode(64 + group.number)} (${group.number})`;

    // Same "pull the label/count out of the block, plain divider row" move
    // as Участники's compact-team-header (index.css) - reuses those exact
    // classes rather than a parallel set, so this reads as the same
    // component, not a lookalike. Only under compactTable - the sticky
    // in-table header (with its own PDF-export-friendly sticky label/select
    // positioning) stays exactly as it was otherwise.
    const compactHeader = compactTable && (
        <div className="compact-team-header">
            <span className="compact-team-header-left">
                {/* .compact-team-number - same small/muted style as the
                    #number in Участники's own compact-team-header, not
                    .week-divider-range (tried, reverted) - pinned to this
                    one shared class instead of drifting between the two. */}
                <span className="compact-team-number">{groupLabel}</span>
            </span>
            {is_authorized && (
                <span className="compact-team-header-right">
                        <span className="week-divider-count">{t("Number of participants")}</span>
                    <div style={{ width: "46px" }}>
                        <SelectInput
                            data={playersOptions}
                            default_value={group.number_players}
                            default_text={t("Participants")}
                            setInputValue={(val) => handlePlayersChange(group.id, Number(val))}
                            input_name="number_players"
                            background={true}
                            show_label={false}
                        />
                    </div>
                    <FeatherIcon icon="x" size={18} style={{ cursor: "pointer" }} onClick={handleDeleteGroup} />
                </span>
            )}
        </div>
    );

    return (
        <div key={group.id} className={`pdf-no-break ${groupDimmed ? "search-dim" : ""}`}>
            {compactHeader}
            <div className="table-wrapper">
                <table className="groups-table">
                    <tbody>
                        {!compactTable && (
                        <tr>
                            <th
                                colSpan={viewMode === "standings" ? 8 : playersCount + 5}
                                className={is_authorized ? "authorized-header" : ""}
                                style={{
                                    textAlign: "left",
                                    // public header height matches the "Групповий етап" stage
                                    // .custom_select next to it (~35px) - was the same 15px
                                    // vertical padding as the authorized header, which measured
                                    // visibly taller than that select.
                                    padding: is_authorized ? "20px 10px" : "10px 10px",
                                    position: "relative",
                                    zIndex: 2
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <label
                                        className="black-name"
                                           style={{
                                        margin: 0,
                                        position: "sticky",
                                        left: "10px",
                                        color: is_authorized ? undefined : "#fff"
                                    }}
                                    >{groupLabel}</label>
                                    {is_authorized && (
                                        <div style={{ display: "flex", alignItems: "center", gap: "10px", position: "sticky", right: "10px", paddingLeft: "10px" }}>
                                            {t("Number of participants")}
                                            <div style={{ width: "60px", position: "relative" }}>
                                                <div style={{ position: "absolute", top: "-22px", right: 0, width: "60px" }}>
                                                    <SelectInput
                                                        data={playersOptions}
                                                        default_value={group.number_players}
                                                        default_text={t("Participants")}
                                                        setInputValue={(val) => handlePlayersChange(group.id, Number(val))}
                                                        input_name="number_players"
                                                        background={true}
                                                        show_label={false}
                                                    />
                                                </div>
                                            </div>
                                            <FeatherIcon icon="x" size={18} style={{ cursor: "pointer" }} onClick={handleDeleteGroup} />
                                        </div>
                                    )}
                                </div>
                            </th>
                        </tr>
                        )}
                        <tr className="group-header">
                            <td className="groups-sticky-num"><center>№</center></td>
                            <td className={`groups-sticky-col ${is_authorized ? "authorized" : "unauthorized"} ${!is_authorized ? "module_padding" : ""}`}>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", whiteSpace: "nowrap" }}>
                                    {t("Participants")}
                                </div>
                            </td>
                            {viewMode === "standings"
                                ? (
                                    <>
                                        <td><center>{t("M")}</center></td>
                                        <td><center>{t("W")}</center></td>
                                        <td><center>{t("L")}</center></td>
                                    </>
                                )
                                : playerNumbers.map((number, i) => <td key={i}><center>{number}</center></td>)
                            }
                            <td><center>{t("+/-")}</center></td>
                            <td><center>{t("PTS")}</center></td>
                            <td><center>{t("Pl")}</center></td>
                        </tr>
                    {Array.from({ length: playersCount }, (_, rowIndex) => {
                        const participant = group.players?.[rowIndex];
                        const isThisPicked = pickedPlayer?.groupId === group.id && pickedPlayer?.position === rowIndex + 1;
                        const isPickerTarget = pickerTarget?.groupId === group.id && pickerTarget?.position === rowIndex + 1;
                        const isCellLoading = !!loadingCells[`${group.id}_${rowIndex + 1}`];
                        // No per-row class once the whole group is already dimmed
                        // as one block above - stacking a second, per-cell opacity
                        // on top of the container's own would double-darken it.
                        const searchMatched = participantMatchesSearch(participant);
                        const searchClass = groupDimmed ? "" : searchStateClass(searchMatched);
                        return (
                            <tr key={rowIndex}>
                                <td className={`groups-sticky-num ${searchClass}`}><center>{rowIndex + 1}</center></td>
                                <td
                                    className={`groups-sticky-col ${is_authorized ? "authorized" : "unauthorized"} ${searchClass}`}
                                    data-drop-zone={is_authorized ? "true" : "false"}
                                    onDragOver={e => {
                                        if (is_authorized) {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = "move";
                                        }
                                    }}
                                    onDrop={e => is_authorized && onDropOnCell(e, group.id, rowIndex + 1)}
                                    onClick={() => {
                                        if (isCellLoading) return;
                                        if (pickedPlayer) {
                                            onCellTap(group.id, rowIndex + 1);
                                            return;
                                        }
                                        // A candidate was picked from the still-open panel (no
                                        // slot targeted yet) - any empty cell, including one that
                                        // would otherwise open/retarget the panel below, just
                                        // places them here instead. An occupied cell cancels the
                                        // pick instead of no-oping - was a bare no-op, but the
                                        // panel can stay open across several picks, so clicking
                                        // through a few candidates while just browsing left this
                                        // stuck on and every click on an occupied cell silently
                                        // did nothing.
                                        if (pickedCandidate) {
                                            if (!participant) onPlaceCandidate(group.id, rowIndex + 1);
                                            else onCancelCandidate?.();
                                            return;
                                        }
                                        if (is_authorized && !participant) openSelectModal(group.id, rowIndex + 1);
                                    }}
                                >
                                    {isCellLoading ? (
                                        <div className="module_padding" style={{ display: "flex", alignItems: "center" }}>
                                            <span className="loading-dots">
                                                <span></span><span></span><span></span>
                                            </span>
                                        </div>
                                    ) : participant ? (
                                            <div
                                                draggable={is_authorized && !isTouchDevice && !pickedPlayer && !pickerTarget && !pickedCandidate}
                                                onDragStart={e => onDragStartPlayer(e, group.id, rowIndex + 1)}
                                                onTouchStart={e => { if (!pickerTarget && !pickedCandidate) onPlayerTouchStart(e, group.id, rowIndex + 1, participant); }}
                                                onContextMenu={e => is_authorized && e.preventDefault()}
                                                // Меню открывается для любого зрителя (не только админа) -
                                                // как в сетках (Bracket/BracketClassic): клик по участнику
                                                // всегда показывает меню, а не сразу профиль/переход.
                                                onClick={() => {
                                                    if (pickedPlayer || pickedCandidate) return;
                                                    setActionsModal({ rowIndex, participant });
                                                }}
                                                className={isThisPicked ? "picked" : ""}
                                                style={{
                                                    cursor: !is_authorized ? "default" : (pickedPlayer || pickedCandidate) ? "crosshair" : "grab",
                                                    userSelect: "none",
                                                    WebkitUserSelect: "none",
                                                    WebkitTouchCallout: "none",
                                                    borderRadius: "5px"
                                                }}
                                            >
                                                {tournament?.grade === 1
                                                    ? (
                                                        <div className="module_padding">
                                                        <span className="player black-name cursor">
                                                                    {participant?.first_name && <>{participant?.first_name?.[0]}.&nbsp;</>}
                                                            <span className="uppercase0">{formatLastName(participant.last_name)}</span>
                                                                </span>
                                                        </div>
                                                    )
                                                    : tournament?.grade === 2
                                                        ? (
                                                            <div style={{padding: "7px 0"}}>
                                                                        <span className="player black-name cursor">
                                                                            {participant.players?.[0]?.first_name && <>{participant.players[0].first_name[0]}.&nbsp;</>}
                                                                            <span className="uppercase0">{ participant.players?.[0] && formatLastName(participant.players[0]?.last_name)}</span>
                                                                        </span>
                                                                <br/>
                                                                <span className="player black-name cursor">
                                                                            {participant.players?.[1]?.first_name && <>{participant.players[1].first_name[0]}.&nbsp;</>}
                                                                    <span className="uppercase0">{ participant.players?.[1] && formatLastName(participant.players[1]?.last_name)}</span>
                                                                        </span>
                                                            </div>
                                                        )
                                                        : (
                                                            <div className="module_padding">
                                                            <span className="player black-name cursor">
                                                                        {participant.name || getTeamName(participant, t, rowIndex + 1)}
                                                            </span></div>
                                                        )
                                                }
                                            </div>
                                        )
                                        : is_authorized
                                            ? (
                                                <span className={`player cursor group-add-participant ${(pickedPlayer || pickedCandidate) ? "drop-target" : ""} ${isPickerTarget ? "picker-target" : ""}`}>
                                                    {(pickedPlayer || pickedCandidate)
                                                        ? t("Drop here")
                                                        : <>+ {t("Participant")}</>
                                                    }
                                                </span>
                                            )
                                            : <div className="module_padding">{t("Participant")} #{rowIndex + 1}</div>
                                    }
                                </td>

                                {viewMode === "standings" ? (
                                    (() => {
                                        const { played, wins, losses } = getRowStats(rowIndex);
                                        return (
                                            <>
                                                <td className={`groups-stat-cell ${searchClass}`}><center>{participant ? played : ""}</center></td>
                                                <td className={`groups-stat-cell ${searchClass}`}><center className="score-win">{participant ? wins : ""}</center></td>
                                                <td className={`groups-stat-cell ${searchClass}`}><center className="score-lose">{participant ? losses : ""}</center></td>
                                            </>
                                        );
                                    })()
                                ) : (
                                <>
                                {/* MATRIX style={{ color: "#a5a3ae", padding: "10px 5px" }} */}
                                {playerNumbers.map((_, colIndex) => {
                                    if (rowIndex === colIndex) return <td key={colIndex} className={`ball ${searchClass}`}><center><BallIcon /></center></td>;
                                    const { match, index } = getMatch(group, rowIndex, colIndex);
                                    const hasScores = match?.sets?.some(s => s.game_score_1 != null && s.game_score_2 != null);

                                    if (!match || !hasScores) {
                                        const mIndex = matchOffset + index;
                                        let label = is_authorized ? `M${mIndex + 1}` : null;

                                        if (match?.winner_side) {
                                            const side = rowIndex < colIndex ? 1 : 2;
                                            label = match.winner_side === side ? "+/-" : "-/+";
                                        }

                                        return (
                                            <td
                                                key={colIndex}
                                                onClick={() => {
                                                    expandMatch(match?.id ?? `${group.id}-${index + 1}`);
                                                }}
                                                className={`cursor ${searchClass}`}
                                            >
                                                <center className={match?.winner_side ? (match.winner_side === (rowIndex < colIndex ? 1 : 2) ? "score-win" : "score-lose") : ""}>
                                                    {match?.winner_side ? label : (is_authorized ? <span className="square-no-thumbnail groups-square-btn">+</span> : label)}
                                                </center>
                                            </td>
                                        );
                                    }

                                    // Score format is per-match, not dictated by the stage's
                                    // games_in_set setting - an admin can add a 2nd/3rd set to
                                    // any match regardless of format (see MatchScoreColumns.js),
                                    // so whether this cell shows a sets-won tally or a single
                                    // game score has to follow what's actually in match.sets.
                                    const isMultiSet = (match.sets || []).filter(s => s.game_score_1 != null || s.game_score_2 != null).length > 1;

                                    let score = "";
                                    if (isMultiSet) {
                                        // Sets-won tally (2-1, 2-0, 1-1 mid-match) for multi-set/best-of-3 matches
                                        let side1Sets = 0, side2Sets = 0;
                                        (match.sets || []).forEach(set => {
                                            if (set.game_score_1 == null || set.game_score_2 == null) return;
                                            if (set.game_score_1 > set.game_score_2) side1Sets++;
                                            else if (set.game_score_2 > set.game_score_1) side2Sets++;
                                        });
                                        score = rowIndex < colIndex ? `${side1Sets}-${side2Sets}` : `${side2Sets}-${side1Sets}`;
                                    } else {
                                        // Single-set game score (e.g. 6-3 or 7-6)
                                        score = (match.sets || []).map(set => {
                                            if (set.game_score_1 == null && set.game_score_2 == null) return null;
                                            let p1 = set.game_score_1 ?? (match.winner_side === 1 ? "+" : "-");
                                            let p2 = set.game_score_2 ?? (match.winner_side === 2 ? "+" : "-");

                                            const isNum = v => /^\d+/.test(String(v));
                                            const sep = isNum(p1) && isNum(p2) ? "-" : "";

                                            return rowIndex < colIndex ? `${p1}${sep}${p2}` : `${p2}${sep}${p1}`;
                                        }).filter(Boolean).join(" ");
                                    }

                                    return (
                                        <td
                                            key={colIndex}
                                            onClick={() => {
                                                expandMatch(match?.id ?? `${group.id}-${index + 1}`);
                                            }}
                                            className={`cursor matches-points ${searchClass}`}
                                            // style={{ cursor: "pointer", textAlign: "center", whiteSpace: "nowrap", padding: "10px 5px" }}
                                        >
                                            <center
                                                className={
                                                    match?.winner_side
                                                        ? (match.winner_side === (rowIndex < colIndex ? 1 : 2) ? "score-win" : "score-lose")
                                                        : ""
                                                }
                                            >
                                                {score}
                                            </center>
                                        </td>
                                    );
                                })}
                                </>
                                )}

                                <td className={`groups-stat-cell ${searchClass}`}>
                                    <center>
                                        {participant?.games != null && <span className="matches-points77">{participant.games > 0 && "+"}{participant.games}</span>}
                                    </center>
                                </td>
                                <td className={`groups-stat-cell ${searchClass}`}>
                                    <center>
                                        {participant?.points != null && <>{participant.points}</>}
                                    </center>
                                </td>
                                <td className={`groups-stat-cell ${searchClass}`}>
                                    <center>
                                        {is_authorized ? (
                                            startPlace ? (
                                                // This group's stage is a "place distribution" one -
                                                // options are constrained to this group's own place
                                                // range (startPlace..startPlace+playersCount-1), still
                                                // a manual pick (not computed from points/games), since
                                                // which participant actually took which place is a
                                                // judgment call an admin makes after group play finishes
                                                // (tournaments differ on tie-break rules).
                                                <div style={{ width: "70px", margin: "0 auto" }}>
                                                    <SelectInput
                                                        data={placeOptions}
                                                        default_value={participant?.place ?? null}
                                                        default_text={t("Place")}
                                                        show_label={false}
                                                        disabled={!participant}
                                                        setInputValue={async (val) => {
                                                            if (!participant) return;
                                                            const newPlace = Number(val);
                                                            const payload = {
                                                                group_id: group.id,
                                                                place: newPlace,
                                                                ...(tournament?.grade === 1 ? { player_id: participant.id ? participant.id : participant.player_id } : { team_id: participant.id ? participant.id : participant.team_id })
                                                            };
                                                            await api.patch("/api/v1.0/group-place/", payload);
                                                            setGroups(prev => prev.map(g => g.id !== group.id ? g : { ...g, players: g.players.map((p, i) => i === rowIndex ? { ...p, place: newPlace } : p) }));
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                // Regular (non place-distribution) group stage - free
                                                // input, same as before. Used to record each
                                                // participant's finishing position within the group for
                                                // seeding into the next bracket, not a final tournament
                                                // place - no range to constrain it to here.
                                                <input
                                                    style={{ width: "36px", textAlign: "center" }}
                                                    type="number"
                                                    min={1}
                                                    value={placeDrafts[rowIndex] ?? (participant?.place ?? "")}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setPlaceDrafts(prev => ({ ...prev, [rowIndex]: val }));
                                                    }}
                                                    onBlur={async (e) => {
                                                        setPlaceDrafts(prev => {
                                                            const next = { ...prev };
                                                            delete next[rowIndex];
                                                            return next;
                                                        });
                                                        if (!participant) return;
                                                        const raw = e.target.value;
                                                        if (raw === "") return;
                                                        const newPlace = Number(raw);
                                                        if (isNaN(newPlace) || newPlace === participant?.place) return;
                                                        const payload = {
                                                            group_id: group.id,
                                                            place: newPlace,
                                                            ...(tournament?.grade === 1 ? { player_id: participant.id ? participant.id : participant.player_id } : { team_id: participant.id ? participant.id : participant.team_id })
                                                        };
                                                        await api.patch("/api/v1.0/group-place/", payload);
                                                        setGroups(prev => prev.map(g => g.id !== group.id ? g : { ...g, players: g.players.map((p, i) => i === rowIndex ? { ...p, place: newPlace } : p) }));
                                                    }}
                                                />
                                            )
                                        ) : (
                                            <div>{participant?.place}</div>
                                        )}
                                    </center>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
            <ParticipantActionsModal
                isOpen={!!actionsModal}
                onClose={() => setActionsModal(null)}
                title={tournament?.grade === 2 ? t("Participants") : t("Participant")}
                actions={actionsModal ? [
                    ...getParticipantProfiles(actionsModal.participant).map(p => ({ icon: "user", label: p.label, onClick: p.onClick })),
                    // Меню теперь открывается и для обычного зрителя (не только
                    // админа) - удаление/перемещение участника из группы
                    // остаётся только для is_authorized, иначе публичный
                    // пользователь получил бы доступ к этим действиям через
                    // этот же список. "Move" hands off into the same
                    // pickedPlayer drag flow the long-press (onPlayerTouchStart)
                    // already starts directly on touch - offered here too so
                    // it doesn't require knowing that gesture, same reasoning
                    // as Bracket.js/BracketClassic.js's own "Move" menu entry.
                    ...(is_authorized ? [{
                        icon: "move",
                        label: t("Move"),
                        onClick: () => onStartMove(group.id, actionsModal.rowIndex + 1, actionsModal.participant)
                    }] : []),
                    ...(is_authorized ? [{
                        icon: "trash-2",
                        label: t("Delete"),
                        danger: true,
                        onClick: async () => {
                            const { rowIndex, participant } = actionsModal;
                            setActionsModal(null);
                            setGroups(prev =>
                                prev.map(g => {
                                    if (g.id !== group.id) return g;
                                    return {
                                        ...g,
                                        players: g.players.map((p, i) => (i === rowIndex ? null : p))
                                    };
                                })
                            );
                            try {
                                const deleteId = getParticipantDeleteId(participant);
                                if (deleteId) {
                                    await api.delete(`/api/v1.0/group-stage/${activeStage}/groups/${group.id}/players/${deleteId}/`);
                                }
                            } catch (e) {
                                console.error("Failed to delete participant from group:", e);
                            } finally {
                                await fetchParticipants(activeStage);
                                await fetchGroups(activeStage);
                            }
                        }
                    }] : [])
                ] : []}
            />
        </div>
    );
};

// memo: this renders once per group ("Всі групи" can mount several at
// once), and its own parent (TournamentGroups.js) re-renders on state that
// has nothing to do with any one group (search chips, the add-participant
// modal opening, wide-viewport layout changes...) - without this, every one
// of those re-rendered every mounted group's whole table too. Only pays off
// because TournamentGroups.js also stabilized (useCallback) every function
// prop passed here, and this component now manages its own `api` (see the
// comment on that above) instead of receiving TournamentGroups' own
// per-render-fresh useAxios() instance as a prop - either gap alone would
// fail the shallow prop comparison below every time regardless of anything
// else being stable. Same reasoning as Bracket.js/BracketClassic.js's own
// identical pass.
export default React.memo(TournamentGroupTable);
